/**
 * RmmPrescriptionModal.tsx
 *
 * Write a prescription and send it to Partell (RequestMyMeds).
 *
 * Items are PICKED FROM THE CATALOGUE, the same way Axiom and SiPhox work. The
 * drug name and its default directions come from the product; nothing about the
 * medicine is typed.
 *
 * DAY SUPPLY HAS NO CATALOGUE COLUMN OF ITS OWN, but some sigs state it in
 * words ("...for 60 days"), and lib/daySupply pulls that number out to
 * pre-fill the field. A sig that does not say seeds `0.1` — not a real
 * supply, so it reads as unanswered rather than as a guess. It is owed on
 * every script either way.
 *
 * THE ICD-10 IS ASKED ONLY WHEN A LINE IS A CONTROLLED SUBSTANCE. The vendor
 * requires a diagnosis code on controlled substances and nothing else (API
 * v2.2), and this catalogue currently carries none — asking for it on every
 * order made every script wait on a field with nothing to put in it. Which
 * lines are controlled is decided from the drug NAME by lib/controlledSubstances
 * (mirrored on the server), because a script read off a PDF is free text that
 * never touched the catalogue.
 *
 * When it is owed, the ICD-10 is a DIAGNOSIS — a property of the patient, not
 * of the drug — so it is asked ONCE for the order and applied to every line,
 * with a per-line override on the controlled rows. The code describes the
 * CONDITION: several drugs treating one diagnosis all carry the same one, and a
 * line treating something else changes its own rather than retyping the common
 * case four times.
 *
 * THE SAME FORM AS AXIOM. The modal is LabOrderModalCore — the Axiom order
 * form — with Partell's parts in its items area: patient details and ship-to
 * first (the core's, locked to the chart), then shipping, the prescriptions,
 * and the prescriber last; "Send Order" at the top, a confirmation step, a
 * success screen. Only what Partell needs differs.
 *
 * ONE PRESCRIBER, MANY SCRIPTS. The vendor's payload repeats the prescriber and
 * patient on every line and groups them by a shared prescription_order_id; the
 * server does that repetition, so this collects the prescriber once.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, ClipboardList, Stethoscope } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { oneLineSig } from './orderFormParts';
import { useToast } from '@/hooks/useToast';
import { useAuthStore } from '@/store/authStore';
import {
  createIntegrationOrder,
  updateIntegrationOrder,
  type CreateIntegrationOrderResult,
  type IntegrationOrder,
  getEnabledIntegrations,
  getIntegrationPrescribers,
  getIntegrationOrderDefaults,
  type IntegrationProduct,
  type IntegrationPrescriber,
  type RmmPrescriberPayload,
  type RmmRxItemPayload,
  type RmmShippingPayload,
} from '@/services/integrations.service';
import { getPatient, updatePatient } from '@/services/patients.service';
import { listFacilities } from '@/services/admin.service';
import { controlledSchedule } from '@/lib/controlledSubstances';
import { defaultDaySupply } from '@/lib/daySupply';
import { toStateCode } from '@/lib/usStates';
import { LabOrderModalCore, type InitialShipTo, type LabOrderConfig, type LabOrderSubmitResult, type ShipToChoice } from './LabOrderModalCore';
import { OrderSection } from './orderFormParts';

/**
 * What one script costs — mirrors RmmIntegration::lineAmount on the server.
 * `unit` items ("$1.25 / Capsule") are price × quantity; `flat` (the whole
 * script) and `container` (one jar/tube) are the price as listed. Null when a
 * per-unit item's quantity isn't a positive number yet.
 */
export function rmmLineAmount(price: number, basis: string | undefined, quantity: string): number | null {
  if ((basis ?? '').trim().toLowerCase() !== 'unit') return Math.round(price * 100) / 100;
  const m = /^\s*(\d+(?:\.\d+)?)/.exec(quantity);
  if (!m || Number(m[1]) <= 0) return null;
  return Math.round(price * Number(m[1]) * 100) / 100;
}

const usd = (n: number) => `$${n.toFixed(2)}`;

/**
 * A row on screen. `integrationProductId` ties the row back to the catalogue
 * entry that created it, so unticking the item removes its row and the picker
 * stays in step — and it is sent with the script, because it is what the
 * server prices the line at. Day supply
 * and ICD-10 are always strings here (an input needs a value); they are
 * dropped from the payload when blank.
 */
type RxRow = Omit<RmmRxItemPayload, 'daySupply' | 'icd10'> & {
  integrationProductId: number;
  daySupply: string;
  icd10: string;
};

/**
 * Read a practice address out of the one free-text line `facilities` stores.
 *
 * The table has a single `address` column — no city/state/zip — and Partell
 * rejects the order without all four separately. The code used to read
 * `facility.city`, a column that does not exist, so the city, state and ZIP
 * arrived empty on every order and had to be retyped each time.
 *
 * Two shapes cover the real rows. When a comma precedes the state, the text
 * before it is street[, city]. When there is no comma — "8570 W Sunset Blvd
 * West Hollywood, CA 90069" — the street TYPE is the delimiter: whatever
 * follows "Blvd" is the city. Unit designators between the two ("Suite 110")
 * belong to the street, not the city.
 *
 * Where the rule does not apply it returns the whole line as the street and
 * leaves the rest blank rather than guessing. An invented city looks answered,
 * and a prescription ships from it.
 */
const STREET_TYPES = /\b(?:st|street|ave|avenue|blvd|boulevard|rd|road|dr|drive|ln|lane|way|pkwy|parkway|ct|court|pl|place|ter|terrace|hwy|highway|cir|circle|trl|trail|sq|square|loop|row|walk|alley|aly)\b\.?/i;
const UNIT_DESIGNATORS = /^(?:suite|ste|unit|apt|apartment|no|number|fl|floor|bldg|building|rm|room|#)\b/i;

export function splitFacilityAddress(raw: string | null | undefined): {
  line1: string; city: string; state: string; zip: string;
} {
  const text = (raw ?? '').trim();
  const empty = { line1: '', city: '', state: '', zip: '' };
  if (text === '') return empty;

  // The state and ZIP live at the end: "…, CA 90069" or "…, CA 90069-1234".
  const tail = text.match(/,?\s*([A-Za-z]{2})\s+(\d{5})(?:-\d{4})?\s*$/);
  if (!tail) return { ...empty, line1: text };

  const head = text.slice(0, tail.index).replace(/[\s,]+$/, '');
  const state = tail[1].toUpperCase();
  const zip = tail[2];

  // A comma in the head marks the city outright — nothing to infer.
  const comma = head.lastIndexOf(',');
  if (comma !== -1) {
    return {
      line1: head.slice(0, comma).trim(),
      city: head.slice(comma + 1).trim(),
      state,
      zip,
    };
  }

  // No comma: the street type delimits. Everything after it — minus any unit
  // designator — is the city.
  const words = head.split(/\s+/);
  let cut = -1;
  for (let i = words.length - 1; i >= 0; i--) {
    if (STREET_TYPES.test(words[i])) { cut = i; break; }
  }
  if (cut === -1 || cut === words.length - 1) return { ...empty, line1: head, state, zip };

  let rest = words.slice(cut + 1);
  // "Suite 110 Santa Monica" → drop "Suite 110".
  while (rest.length > 1 && UNIT_DESIGNATORS.test(rest[0])) {
    rest = /^\d|^#/.test(rest[1]) ? rest.slice(2) : rest.slice(1);
  }
  // Nothing recognisable left, or a lone number: do not invent a city.
  if (rest.length === 0 || rest.every((w) => /^[\d#-]+$/.test(w))) {
    return { ...empty, line1: head, state, zip };
  }

  return {
    line1: words.slice(0, cut + 1).concat(words.slice(cut + 1, words.length - rest.length)).join(' ').trim(),
    city: rest.join(' '),
    state,
    zip,
  };
}


/** A script for one catalogue item. Ship-to-patient at 2-day is the ordinary case. */

function rxFor(p: IntegrationProduct): RxRow {
  // The pharmacy's own directions for this product, editable like any other
  // field — it is a starting point, not a decision.
  const sig = String((p.metadata as { defaultSig?: string } | undefined)?.defaultSig ?? '');
  return {
    integrationProductId: p.integrationProductId,
    drug: p.name,
    sig,
    quantity: '1',
    refills: '0',
    // The sig itself says the day supply for some scripts ("...for 60
    // days") — pre-fill it rather than making someone retype a number
    // already sitting in the directions. `0.1` when the sig does not say:
    // not a real supply, so it cannot be mistaken for one, but it is a
    // number, so it does not block on the day-supply-is-required rule while
    // someone looks up the real figure.
    daySupply: defaultDaySupply(sig),
    icd10: '',
    // Always dispensed as written — no substitution (Adan, 2026-09-29).
    dispenseAsWritten: 'Yes',
  };
}

/**
 * A catalogue category as staff read it. The pharmacy's sheet runs some words
 * together ("WEIGHTLOSS"); shown with the space.
 */
const CATEGORY_LABELS: Record<string, string> = { WEIGHTLOSS: 'WEIGHT LOSS' };
export const categoryLabel = (c: string): string => CATEGORY_LABELS[c.toUpperCase()] ?? c;

/**
 * The one state a product may ship to, or null when it ships anywhere. Some
 * Partell products are sold for one state only and say so in their name —
 * "… 4mL Vial (California Only)". `metadata.availableStates` wins when an item
 * carries it (a list, first entry used). Mirrored on the server
 * (RmmIntegration::onlyState) so a stale form cannot get past it.
 */
export function onlyStateOf(p: { name: string; metadata?: unknown }): string | null {
  const listed = (p.metadata as { availableStates?: unknown } | undefined)?.availableStates;
  if (Array.isArray(listed) && listed.length === 1) return toStateCode(String(listed[0]));
  const m = /\(\s*([A-Za-z .]+?)\s+only\s*\)/i.exec(p.name);
  return m ? toStateCode(m[1]) : null;
}

/** Full state name for the lock label: "California only". */
const STATE_NAMES: Record<string, string> = { CA: 'California' };
const onlyLabel = (code: string) => `${STATE_NAMES[code] ?? code} only`;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  patientId: number;
  /**
   * Called after a successful submit, with what the server created — the
   * caller needs `fulfillmentStatus` to know whether the prescription is being
   * held for payment rather than already on its way.
   */
  onSubmitted: (created: CreateIntegrationOrderResult) => void;
  /**
   * Start from an existing order's scripts — Re-order, Clone and Re-send, the
   * same three the peptide form offers. The scripts are re-read from today's
   * catalogue (a product since removed is left out, and said so); every field
   * stays editable, because a re-order is a new prescription.
   */
  seedOrder?: IntegrationOrder | null;
  /** Re-use this order's ship-to (Clone / Re-send); a plain Re-order proposes the latest address instead. */
  shipFromOrder?: IntegrationOrder | null;
  /** The cancelled order a Re-send replaces, for the activity log on both. */
  resentFromOrderId?: number | null;
  /**
   * Edit in place, before the pharmacy has it. Only the delivery address can
   * change: a signed prescription's drug, directions and quantity are not
   * edited, they are re-written (cancel → Re-send) — the server allows nothing
   * else on a Partell order.
   */
  editOrder?: IntegrationOrder | null;
  onUpdated?: () => void;
}

/** The ship-to an order carries, in the form's seed shape. */
function shipToOf(order: IntegrationOrder | null | undefined): InitialShipTo | null {
  const a = (order?.metadata as { shippingAddress?: InitialShipTo } | null | undefined)?.shippingAddress;
  return a && typeof a === 'object' && a.line1 ? a : null;
}

export function RmmPrescriptionModal({ isOpen, onClose, patientId, onSubmitted, seedOrder = null, shipFromOrder = null, resentFromOrderId = null, editOrder = null, onUpdated }: Props) {
  const isEdit = !!editOrder;
  const toast = useToast();
  // Selected as primitives, never as the user object: this effect writes state,
  // so depending on an object identity that changes on any store update would
  // re-seed on every render and loop.
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const userFirstName = useAuthStore((s) => s.user?.firstName ?? '');
  const userLastName = useAuthStore((s) => s.user?.lastName ?? '');

  const [items, setItems] = useState<RxRow[]>([]);
  // E29.1 (testicular hypofunction) is the code every controlled hormone
  // script in this catalogue is written under today — HCG, testosterone.
  // Pre-filled so the common case needs no typing; still a plain text field.
  const [orderIcd10, setOrderIcd10] = useState('E29.1');
  const [icd10Overrides, setIcd10Overrides] = useState<Record<number, boolean>>({});
  /**
   * Where this order ships, decided once. The vendor takes these per line and
   * the server fans them out, but one order is one shipment. The patient
   * ship-to itself is the form's own Ship to (LabOrderModalCore), as on Axiom.
   */
  /**
   * Picked in the form's own Ship to menu: the patient's addresses, or
   * "Clinic" — the practice address. Speed is always 2-day.
   */
  const [shipChoice, setShipChoice] = useState<string | null>(null);
  const shipType: RmmShippingPayload['shipType'] = shipChoice === 'clinic' ? 'ship_to_clinic' : 'ship_to_patient';
  /** The ship-to state as the form reports it (the clinic's, when shipping there). */
  const [shipState, setShipState] = useState<string | null>(null);
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState('');
  const [prescriber, setPrescriber] = useState<RmmPrescriberPayload>({
    npi: '',
    firstName: '',
    lastName: '',
    address: { line1: '', city: '', state: '', zip: '' },
    phone: '',
    fax: '',
    clinicName: '',
  });
  // The practice address is right nearly always, so it is shown as a line of
  // text rather than six inputs. Editable on demand.
  const [editingAddress, setEditingAddress] = useState(false);

  /**
   * The Partell catalogue. Already carried by the enabled-integrations payload,
   * so the picker costs no extra request.
   */
  const { data: integrations = [] } = useQuery({
    queryKey: ['integrations', 'enabled'],
    queryFn: getEnabledIntegrations,
    enabled: isOpen,
    staleTime: 5 * 60_000,
  });
  const catalogue = (integrations.find((i) => i.key === 'rmm')?.products ?? [])
    .filter((p) => (p as { active?: boolean }).active !== false);

  /** The account's prescribers, the same list the Axiom form picks from. */
  const { data: prescribers = [] } = useQuery({
    queryKey: ['integration-prescribers'],
    queryFn: getIntegrationPrescribers,
    enabled: isOpen,
    staleTime: 5 * 60_000,
  });

  // The patient's plan doctor — the same default the Axiom form uses.
  const { data: orderDefaults } = useQuery({
    queryKey: ['integration-order-defaults', 'rmm', patientId],
    queryFn: () => getIntegrationOrderDefaults(patientId, 'rmm'),
    enabled: isOpen,
    staleTime: 60_000,
  });

  // The prescriber's address is the practice's: it comes from the patient's
  // facility — the location this script is being written at.
  const { data: patient } = useQuery({
    queryKey: ['patient', patientId],
    queryFn: () => getPatient(patientId),
    enabled: isOpen,
    staleTime: 5 * 60_000,
  });
  const { data: facilities = [] } = useQuery({
    queryKey: ['facilities'],
    queryFn: listFacilities,
    enabled: isOpen,
    staleTime: 5 * 60_000,
  });
  const facility = facilities.find((f) => f.facilityId === patient?.facilityId) ?? facilities[0];

  const applyPrescriber = (id: number) => {
    const p = prescribers.find((x: IntegrationPrescriber) => x.userId === id);
    if (!p) return;
    setPrescriber((prev) => ({
      ...prev,
      userId: p.userId,
      firstName: p.firstName,
      lastName: p.lastName,
      npi: p.npi,
      phone: p.phone || prev.phone,
    }));
  };

  /**
   * A default prescriber, picked once per opening: the patient's plan doctor
   * (matched by name, as the Axiom form does), else the signed-in user if they
   * prescribe, else the only prescriber there is. Always changeable.
   */
  const prescriberDefaultedRef = useRef(false);
  useEffect(() => { if (!isOpen) prescriberDefaultedRef.current = false; }, [isOpen]);
  useEffect(() => {
    if (!isOpen || prescriberDefaultedRef.current || prescribers.length === 0 || orderDefaults === undefined) return;
    prescriberDefaultedRef.current = true;
    const wanted = (orderDefaults?.providerName ?? '').trim().replace(/^dr\.?\s+/i, '').toLowerCase();
    const pick =
      (wanted ? prescribers.find((p: IntegrationPrescriber) => `${p.firstName} ${p.lastName}`.trim().toLowerCase() === wanted) : undefined)
      ?? prescribers.find((p: IntegrationPrescriber) => p.userId === userId)
      ?? (prescribers.length === 1 ? prescribers[0] : undefined);
    if (pick) applyPrescriber(pick.userId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, prescribers, orderDefaults]);

  // Reset on every opening, and seed the prescriber name from the signed-in
  // user until a default is picked — every field stays editable.
  useEffect(() => {
    if (!isOpen) return;
    // Nothing is prescribed until something is picked.
    setItems([]);
    setOrderIcd10('E29.1');
    setIcd10Overrides({});
    setShipChoice(null);
    setShipState(null);
    setSearch('');
    setOpenCategories({});
    setPrescriber((p) => ({
      ...p,
      userId,
      firstName: p.firstName || userFirstName,
      lastName: p.lastName || userLastName,
    }));
  }, [isOpen, userId, userFirstName, userLastName]);

  // Seed the practice address once the facility resolves, without overwriting
  // anything already typed.
  useEffect(() => {
    if (!isOpen || !facility) return;
    // `facilities` stores one free-text line, so the four fields the pharmacy
    // needs are read out of it.
    const parsed = splitFacilityAddress(facility.address);
    setPrescriber((p) => ({
      ...p,
      clinicName: p.clinicName || facility.facilityName || '',
      address: {
        line1: p.address.line1 || parsed.line1,
        city: p.address.city || parsed.city,
        state: p.address.state || parsed.state,
        zip: p.address.zip || parsed.zip,
      },
      phone: p.phone || (facility.phone ?? ''),
    }));
  }, [isOpen, facility]);

  /** Catalogue grouped into the collapsible sections the picker renders. */
  const sections = (() => {
    const q = search.trim().toLowerCase();
    const by = new Map<string, IntegrationProduct[]>();
    for (const p of catalogue) {
      if (q && !p.name.toLowerCase().includes(q)) continue;
      const c = String((p.metadata as { category?: string } | undefined)?.category ?? '').trim() || 'OTHER';
      if (!by.has(c)) by.set(c, []);
      by.get(c)!.push(p);
    }
    return [...by.entries()].sort(([a], [b]) => a.localeCompare(b));
  })();

  const chosen = new Set(items.map((it) => it.integrationProductId));

  /** Tick adds the script, untick removes it — the picker and the rows are one list. */
  const toggleProduct = (p: IntegrationProduct) =>
    setItems((prev) => prev.some((it) => it.integrationProductId === p.integrationProductId)
      ? prev.filter((it) => it.integrationProductId !== p.integrationProductId)
      // The order's diagnosis only ever applies to a CONTROLLED line.
      : [...prev, { ...rxFor(p), ...(controlledSchedule(p.name) !== null ? { icd10: orderIcd10 } : {}) }]);

  /**
   * Seed the scripts from an existing order (Re-order / Clone / Re-send / Edit),
   * once per opening and once the catalogue is in. Each line is re-read from
   * today's catalogue so price and product are current; the order's own
   * directions, quantity, refills, supply and diagnosis are kept.
   */
  const seedSource = editOrder ?? seedOrder;
  const seededRef = useRef<number | null>(null);
  useEffect(() => { if (!isOpen) seededRef.current = null; }, [isOpen]);
  useEffect(() => {
    if (!isOpen || !seedSource || catalogue.length === 0) return;
    if (seededRef.current === seedSource.integrationOrderId) return;
    seededRef.current = seedSource.integrationOrderId;
    const missing: string[] = [];
    const rows: RxRow[] = [];
    for (const it of seedSource.items ?? []) {
      const p = catalogue.find((c) => c.integrationProductId === it.integrationProductId)
        ?? catalogue.find((c) => c.name.trim().toLowerCase() === (it.name ?? '').trim().toLowerCase());
      if (!p) { missing.push(it.name); continue; }
      if (rows.some((r) => r.integrationProductId === p.integrationProductId)) continue;
      const base = rxFor(p);
      rows.push({
        ...base,
        sig: it.sig?.trim() || base.sig,
        quantity: it.quantity?.trim() || base.quantity,
        refills: it.refills?.trim() || base.refills,
        daySupply: it.daySupply?.trim() || base.daySupply,
        icd10: it.icd10?.trim() || base.icd10,
      });
    }
    setItems(rows);
    // Open the sections the seeded scripts live in, so they are on screen to
    // review rather than hidden under collapsed categories.
    const cats: Record<string, boolean> = {};
    for (const r of rows) {
      const p = catalogue.find((c) => c.integrationProductId === r.integrationProductId);
      cats[String((p?.metadata as { category?: string } | undefined)?.category ?? '').trim() || 'OTHER'] = true;
    }
    setOpenCategories((prev) => ({ ...prev, ...cats }));
    if (missing.length > 0 && !isEdit) {
      toast.error(`No longer in the Partell catalogue, left out: ${missing.join(', ')}`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, seedSource, catalogue.length]);

  const patchItem = (i: number, patch: Partial<RmmRxItemPayload>) =>
    setItems((prev) => prev.map((it, n) => (n === i ? { ...it, ...patch } : it)));

  /** Whether this product can't ship to where the order is going. */
  const stateLocked = (p: { name: string; metadata?: unknown }): string | null => {
    const only = onlyStateOf(p);
    return only && shipState && shipState !== only ? only : null;
  };

  // Picking a destination a picked product can't ship to takes it off the
  // order — it could never be sent — and says so.
  useEffect(() => {
    if (!isOpen || !shipState) return;
    const off = items.filter((it) => stateLocked({ name: it.drug }));
    if (off.length === 0) return;
    setItems((prev) => prev.filter((it) => !stateLocked({ name: it.drug })));
    toast.error(`Removed — not available in ${shipState}: ${off.map((it) => it.drug).join(', ')}`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, shipState]);

  /** The order's code reaches every line that has not overridden it. */
  const applyOrderIcd10 = (next: string) => {
    setItems((prev) =>
      prev.map((it) => (icd10Overrides[it.integrationProductId] || controlledSchedule(it.drug) === null
        ? it
        : { ...it, icd10: next })));
    setOrderIcd10(next);
  };

  /**
   * What Partell needs beyond the form's own checks (patient, ship-to), run by
   * "Send Order". Checked here as well as on the server because the vendor's
   * failure mode is a flat 400 with no field name.
   */
  const problems: string[] = [];
  if (items.length === 0) problems.push('At least one prescription');
  if (!prescriber.npi.trim()) problems.push('Prescriber NPI');
  if (!prescriber.firstName.trim() || !prescriber.lastName.trim()) problems.push('Prescriber name');
  if (!prescriber.phone.trim()) problems.push('Prescriber phone');
  const a = prescriber.address;
  if (!a.line1.trim() || !a.city.trim() || !a.state.trim() || !a.zip.trim()) problems.push('Prescriber address');
  items.forEach((it, i) => {
    const n = i + 1;
    if (!it.drug.trim()) problems.push(`Rx ${n}: drug`);
    if (!it.sig.trim()) problems.push(`Rx ${n}: directions`);
    // Day supply is owed on every script; the ICD-10 only on a controlled one.
    if (!it.daySupply.trim()) problems.push(`Rx ${n}: day supply`);
    if (controlledSchedule(it.drug) !== null && !it.icd10.trim()) problems.push(`Rx ${n}: ICD-10`);
  });
  const anyControlled = items.some((it) => controlledSchedule(it.drug) !== null);
  items.forEach((it) => {
    const only = onlyStateOf({ name: it.drug });
    if (only && shipState !== only) problems.push(`${it.drug} ships to ${only} only`);
  });
  if (shipType === 'ship_to_clinic' && (!a.line1.trim() || !a.city.trim() || !a.state.trim() || !a.zip.trim())) {
    problems.push('Practice address (the clinic ships to it)');
  }

  // The form's config is built once; these refs carry the current values into it.
  const itemsRef = useRef(items); itemsRef.current = items;
  const prescriberRef = useRef(prescriber); prescriberRef.current = prescriber;
  const problemsRef = useRef(problems); problemsRef.current = problems;
  const onSubmittedRef = useRef(onSubmitted); onSubmittedRef.current = onSubmitted;
  const editRef = useRef(editOrder); editRef.current = editOrder;
  const resentRef = useRef(resentFromOrderId); resentRef.current = resentFromOrderId;
  // A plain Re-order records the order it repeats (see AxiomOrderModal).
  const reorderOfId = !editOrder && seedOrder && !shipFromOrder && !resentFromOrderId ? seedOrder.integrationOrderId : null;
  const reorderOfRef = useRef(reorderOfId); reorderOfRef.current = reorderOfId;
  const onUpdatedRef = useRef(onUpdated); onUpdatedRef.current = onUpdated;

  const config = useMemo<LabOrderConfig>(() => ({
    vendor: 'rmm',
    title: editOrder ? `Edit Partell Prescription #${editOrder.integrationOrderId}` : 'New Partell Prescription',
    successTitle: 'Prescription Created',
    requireAddress: true,
    // Same as Axiom: identity is the chart's, and none of the lab-visit UI applies.
    lockPatientIdentity: true,
    features: {
      presets: false, facilityProvider: false, billing: false, diagnoses: false, tests: false,
      notes: false, collectionDate: false, vitals: false,
    },
    // The destination is picked in the form's own Ship to menu.
    onShipToChoiceChange: (key: string | null) => setShipChoice(key),
    onShipStateChange: (st: string) => setShipState(toStateCode(st)),
    // An edit changes only the ship-to, which the form itself validates.
    validateExtra: () => (editRef.current ? null : problemsRef.current.length > 0 ? `Still needed: ${problemsRef.current.join(', ')}` : null),
    submit: async (payload): Promise<LabOrderSubmitResult> => {
      const editing = editRef.current;
      if (editing) {
        await updateIntegrationOrder(payload.patientId, editing.integrationOrderId, {
          key: 'rmm',
          shippingAddress: {
            recipientName: payload.recipientName || undefined,
            line1: payload.street,
            line2: payload.street2 || undefined,
            city: payload.city,
            state: payload.state,
            zip: payload.zip,
            country: payload.country || undefined,
            patientShippingAddressId: payload.shippingAddressId ?? undefined,
          },
        });
        toast.success('Delivery address updated.');
        onUpdatedRef.current?.();
        return { ok: true, lines: [`Partell prescription #${editing.integrationOrderId}: delivery address updated.`] };
      }
      // A lab order may FILL a missing demographic, never change one.
      if (payload.demographicsFill) {
        await updatePatient(payload.patientId, payload.demographicsFill);
      }
      const rx = itemsRef.current;
      const pr = prescriberRef.current;
      const type: RmmShippingPayload['shipType'] = payload.shipToChoice === 'clinic' ? 'ship_to_clinic' : 'ship_to_patient';
      const created = await createIntegrationOrder(payload.patientId, {
        key: 'rmm',
        // A Re-send says which cancelled order it replaces (activity log on both).
        ...(resentRef.current || reorderOfRef.current
          ? {
            metadata: {
              ...(resentRef.current ? { resentFromOrderId: resentRef.current } : {}),
              ...(reorderOfRef.current ? { reorderOfOrderId: reorderOfRef.current } : {}),
            },
          }
          : {}),
        // The ICD-10 is omitted when blank, not sent as "".
        rxItems: rx.map(({ daySupply, icd10, ...it }) => ({
          ...it,
          // Typed over several lines for readability; sent as one line (see oneLineSig).
          sig: oneLineSig(it.sig),
          daySupply: daySupply.trim(),
          ...(icd10.trim() ? { icd10: icd10.trim() } : {}),
        })),
        prescriber: pr,
        // Shipping is sent once; the server writes it onto every line.
        shipType: type,
        // Speed is not a choice: always 2-day.
        shipRate: '2_day',
        clinicShipAddress: type === 'ship_to_clinic'
          ? {
              line1: pr.address.line1.trim(),
              city: pr.address.city.trim(),
              state: pr.address.state.trim(),
              zip: pr.address.zip.trim(),
            }
          : null,
        // The form's ship-to, read by RmmIntegration in preference to the chart.
        shippingAddress: type === 'ship_to_patient'
          ? {
              recipientName: payload.recipientName || undefined,
              line1: payload.street,
              line2: payload.street2 || undefined,
              city: payload.city,
              state: payload.state,
              zip: payload.zip,
              country: payload.country || undefined,
              patientShippingAddressId: payload.shippingAddressId ?? undefined,
            }
          : undefined,
      });
      // Not "sent" when it is held for payment.
      const held = created.fulfillmentStatus === 'awaiting_payment';
      toast.success(
        held
          ? 'Prescription saved — take the payment to send it to Partell.'
          : rx.length === 1 ? 'Prescription sent to Partell.' : `${rx.length} prescriptions sent to Partell.`,
      );
      onSubmittedRef.current(created);
      return {
        ok: true,
        lines: [
          held
            ? `Partell prescription #${created.integrationOrderId} saved — it goes to the pharmacy once paid.`
            : `Partell prescription #${created.integrationOrderId} sent to the pharmacy.`,
        ],
      };
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [editOrder?.integrationOrderId]);

  const field = 'text-[11px] font-medium text-slate-600';

  /**
   * The vendor needs the practice address as four separate values; the panel
   * opens whenever any of the four is missing.
   */
  const addressIncomplete = !a.line1.trim() || !a.city.trim() || !a.state.trim() || !a.zip.trim();

  /** Each picked script with what it will cost, by the server's rule. */
  const pricedLines = items.map((it) => {
    const p = catalogue.find((c) => c.integrationProductId === it.integrationProductId);
    const meta = p?.metadata as { unit?: string; priceBasis?: string } | undefined;
    const price = p?.price ?? 0;
    const perUnit = (meta?.priceBasis ?? '').toLowerCase() === 'unit';
    return { it, price, unit: meta?.unit, perUnit, amount: rmmLineAmount(price, meta?.priceBasis, it.quantity) };
  });
  const orderTotal = pricedLines.reduce((sum, l) => sum + (l.amount ?? 0), 0);
  const totalPending = pricedLines.some((l) => l.amount === null);

  /** Edit mode shows the scripts read-only: only the delivery address can change. */
  const editSlot = isEdit ? (
    <OrderSection
      icon={<ClipboardList className="h-3.5 w-3.5 text-slate-400" />}
      title="Prescriptions"
      hint="A written prescription is not edited here. To change a drug, directions or quantity, cancel this order and Re-send it."
    >
      <div className="space-y-2" data-testid="rmm-edit-readonly">
        <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Only the delivery address can be changed before the pharmacy has it. To change a prescription,
          cancel this order and use <b>Re-send</b>.
        </p>
        {(editOrder?.items ?? []).map((it, i) => (
          <div key={i} className="rounded-md border border-slate-200 px-3 py-2 text-sm">
            <p className="font-medium text-slate-800">{it.name}</p>
            {it.sig && <p className="text-xs text-slate-600">{it.sig}</p>}
            <p className="text-[11px] text-slate-500">
              {[it.quantity && `Qty ${it.quantity}`, it.refills && `Refills ${it.refills}`, it.daySupply && `${it.daySupply} days`].filter(Boolean).join(' · ')}
            </p>
          </div>
        ))}
      </div>
    </OrderSection>
  ) : null;

  const itemsSlot = (
    <div className="space-y-4">
      <OrderSection
        icon={<ClipboardList className="h-3.5 w-3.5 text-slate-400" />}
        title="Prescriptions"
        required
        hint="Pick each item from the Partell catalogue; its directions, quantity and day supply are asked for on its row."
      >
        <div className="space-y-3">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search the catalogue…"
            className="max-w-xs"
          />
          {/* The catalogue, one collapsible section per category — the same
              shape the Axiom vials picker uses. Ticking an item writes its
              script below with the pharmacy's own directions already in it. */}
          <div className="rounded-lg border border-slate-200">
            {sections.length === 0 && (
              <p className="px-3 py-4 text-xs text-slate-500">
                {catalogue.length === 0
                  ? 'No Partell catalogue is loaded for this practice.'
                  : 'Nothing matches that search.'}
              </p>
            )}
            {sections.map(([category, products]) => {
              // A search opens every section that still has a match — leaving
              // them shut would hide the very thing being searched for.
              const open = search.trim() !== '' || openCategories[category];
              const picked = products.filter((p) => chosen.has(p.integrationProductId)).length;
              return (
                <div key={category} className="border-b border-slate-200 last:border-b-0">
                  <button
                    type="button"
                    onClick={() => setOpenCategories((m) => ({ ...m, [category]: !m[category] }))}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-slate-50"
                  >
                    {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                    <span className="text-xs font-semibold text-slate-700">{categoryLabel(category)}</span>
                    <span className="text-[11px] text-slate-400">{products.length}</span>
                    {picked > 0 && (
                      <span className="ml-auto rounded-full bg-teal-100 px-2 py-0.5 text-[10px] font-semibold text-teal-800">
                        {picked} selected
                      </span>
                    )}
                  </button>
                  {open && (
                    <ul className="px-3 pb-2">
                      {products.map((p) => {
                        const meta = p.metadata as { unit?: string; priceBasis?: string } | undefined;
                        const isPicked = chosen.has(p.integrationProductId);
                        const itemIndex = items.findIndex((it) => it.integrationProductId === p.integrationProductId);
                        // The row's drug is the catalogue name, so the schedule
                        // is decided here and shown on the row — the prescriber
                        // sees WHY two fields became mandatory.
                        const schedule = controlledSchedule(p.name);
                        // A product sold for one state only, while the order
                        // ships elsewhere: struck through, can't be picked,
                        // labelled — as the Axiom form locks by state.
                        const only = onlyStateOf(p);
                        const locked = stateLocked(p);
                        return (
                          <li key={p.integrationProductId} className="py-1">
                            <div className={`flex items-start gap-2 ${locked && !isPicked ? 'opacity-60' : ''}`}>
                              <input
                                type="checkbox"
                                className="mt-1 disabled:cursor-not-allowed"
                                checked={isPicked}
                                disabled={!!locked && !isPicked}
                                onChange={() => toggleProduct(p)}
                                aria-label={p.name}
                              />
                              <span className="flex flex-1 flex-wrap items-center gap-2 text-sm text-slate-700">
                                <span className={locked ? 'line-through decoration-slate-400' : undefined}>{p.name}</span>
                                {only && (
                                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${locked ? 'bg-red-100 text-red-800' : 'bg-slate-100 text-slate-700'}`}>
                                    {locked ? `Not available in ${shipState} · ${onlyLabel(only)}` : onlyLabel(only)}
                                  </span>
                                )}
                              </span>
                              <span className="whitespace-nowrap text-xs text-slate-500">
                                {p.price > 0 ? `$${p.price.toFixed(2)}` : '—'}
                                {/* The unit is what the price BUYS, and it is not
                                    always one dose: the pharmacy prices some items
                                    per capsule and others per 30-gram jar. */}
                                {meta?.unit ? ` / ${meta.unit}` : ''}
                              </span>
                            </div>

                            {/* The script is written HERE, under the item it is
                                for. It used to accumulate in a separate "Rx 1 /
                                Rx 2" list far below the catalogue, so picking a
                                drug and describing it happened in two places
                                with nothing tying them together — and with ten
                                items the list and the catalogue no longer even
                                shared a screen. Same shape as the Axiom picker,
                                which asks for directions on the row. */}
                            {isPicked && itemIndex !== -1 && (
                              <div className="ml-6 mt-2 space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3">
                                {/* Why the two fields below are mandatory on THIS
                                    row and not on the one above it. */}
                                {schedule && (
                                  <span className="inline-flex items-center rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-700">
                                    Controlled · Schedule {schedule}
                                  </span>
                                )}
                                <label className="block space-y-1">
                                  <span className={field}>Directions (sig) *</span>
                                  <Textarea
                                    rows={3}
                                    autoResize
                                    className="resize-y"
                                    value={items[itemIndex].sig}
                                    onChange={(e) => patchItem(itemIndex, { sig: e.target.value })}
                                    placeholder="Inject 2.5mg subcutaneously once weekly"
                                  />
                                </label>

                                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-4">
                                  <label className="space-y-1"><span className={field}>Quantity *</span>
                                    <Input value={items[itemIndex].quantity} onChange={(e) => patchItem(itemIndex, { quantity: e.target.value })} /></label>
                                  <label className="space-y-1"><span className={field}>Refills *</span>
                                    <Input value={items[itemIndex].refills} onChange={(e) => patchItem(itemIndex, { refills: e.target.value })} /></label>
                                  <label className="space-y-1"><span className={field}>Day supply *</span>
                                    <Input value={items[itemIndex].daySupply} onChange={(e) => patchItem(itemIndex, { daySupply: e.target.value })} placeholder="28" /></label>
                                  {/* The diagnosis is owed only on a controlled line.
                                      It is one decision for the order — it describes
                                      the condition, not the drug — so the row states
                                      the order's code and only becomes a field when
                                      someone says this drug differs. */}
                                  {schedule && (
                                    <div className="space-y-1">
                                      <span className={field}>ICD-10 *</span>
                                      {icd10Overrides[p.integrationProductId] ? (
                                        <>
                                          <Input
                                            value={items[itemIndex].icd10}
                                            onChange={(e) => patchItem(itemIndex, { icd10: e.target.value })}
                                            placeholder="E66.9"
                                            aria-label={`ICD-10 for ${p.name}`}
                                          />
                                          <button
                                            type="button"
                                            className="text-[10px] text-blue-600 hover:underline"
                                            onClick={() => {
                                              setIcd10Overrides((m) => ({ ...m, [p.integrationProductId]: false }));
                                              patchItem(itemIndex, { icd10: orderIcd10 });
                                            }}
                                          >
                                            Use the order&apos;s code
                                          </button>
                                        </>
                                      ) : (
                                        <div className="flex items-baseline gap-2">
                                          <span className="text-sm text-slate-800">
                                            {items[itemIndex].icd10 || <span className="text-amber-700">not set</span>}
                                          </span>
                                          <button
                                            type="button"
                                            className="text-[10px] text-blue-600 hover:underline"
                                            onClick={() => setIcd10Overrides((m) => ({ ...m, [p.integrationProductId]: true }))}
                                          >
                                            Change for this drug
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>

                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>

          {/* One diagnosis for the order, asked only when a line is a controlled
              substance — the vendor's rule. A catalogue cannot supply it: it
              describes the patient, not the drug. */}
          {anyControlled && (
            <div className="space-y-1">
              <label className="block space-y-1">
                <span className={field}>Diagnosis being treated (ICD-10) *</span>
                <Input
                  value={orderIcd10}
                  onChange={(e) => applyOrderIcd10(e.target.value)}
                  placeholder="E66.9"
                  className="max-w-xs"
                />
              </label>
              {/* Outside the <label>: inside, it becomes part of the field's
                  accessible name, which is what screen readers announce. */}
              <p className="text-[11px] text-slate-500">
                Required: this order includes a controlled substance. The code describes
                the condition, not the drug — several medications for one condition share
                it, so it is the same on every prescription in this order. A drug that
                treats something else can be changed on its own row.
              </p>
            </div>
          )}

          {items.length === 0 && catalogue.length > 0 && (
            <p className="rounded-md bg-slate-50 px-3 py-3 text-xs text-slate-500">
              Pick an item above — its directions, quantity and day supply are asked for on the row.
            </p>
          )}

          {/* What the patient will be charged — the same rule the server bills
              by: per-unit items are price × quantity, the rest as listed. */}
          {pricedLines.length > 0 && (
            <div className="space-y-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm" data-testid="rmm-order-total">
              {pricedLines.map((l) => (
                <div key={l.it.integrationProductId} className="flex justify-between gap-3 text-slate-600">
                  <span className="truncate">
                    {l.it.drug}
                    {l.perUnit && <span className="text-xs text-slate-400"> · {usd(l.price)} × {l.it.quantity || '?'} {l.unit ?? 'units'}</span>}
                  </span>
                  <span className="whitespace-nowrap">{l.amount === null ? 'enter quantity' : usd(l.amount)}</span>
                </div>
              ))}
              <div className="flex justify-between border-t border-slate-200 pt-1 font-semibold text-slate-900">
                <span>Total</span>
                <span>{usd(orderTotal)}{totalPending ? ' + pending' : ''}</span>
              </div>
            </div>
          )}
        </div>
      </OrderSection>

      <OrderSection
        icon={<Stethoscope className="h-3.5 w-3.5 text-slate-400" />}
        title="Prescriber"
        required
        hint="The prescribing provider. The pharmacy needs their name, NPI, phone and practice address."
      >
        <div className="space-y-2">

          {prescribers.length > 0 && (
            <label className="block space-y-1">
              <span className={field}>Prescriber *</span>
              <select
                value={prescriber.userId ?? ''}
                onChange={(e) => applyPrescriber(Number(e.target.value))}
                className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
              >
                <option value="">Select a prescriber…</option>
                {prescribers.map((p: IntegrationPrescriber) => (
                  <option key={p.userId} value={p.userId}>
                    {p.firstName} {p.lastName}{p.credential ? `, ${p.credential}` : ''}
                    {p.npi ? '' : ' — no NPI on file'}
                  </option>
                ))}
              </select>
            </label>
          )}

          {/* Still editable by hand: a locum or a prescriber with no record yet
              has to be possible, and the pharmacy needs the NPI either way. */}
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <label className="space-y-1"><span className={field}>First name *</span>
              <Input value={prescriber.firstName} onChange={(e) => setPrescriber({ ...prescriber, firstName: e.target.value })} /></label>
            <label className="space-y-1"><span className={field}>Last name *</span>
              <Input value={prescriber.lastName} onChange={(e) => setPrescriber({ ...prescriber, lastName: e.target.value })} /></label>
            <label className="space-y-1"><span className={field}>NPI *</span>
              <Input value={prescriber.npi} onChange={(e) => setPrescriber({ ...prescriber, npi: e.target.value })} placeholder="10-digit NPI" /></label>
            <label className="space-y-1"><span className={field}>Phone *</span>
              <Input value={prescriber.phone} onChange={(e) => setPrescriber({ ...prescriber, phone: e.target.value })} /></label>
            <label className="space-y-1"><span className={field}>Fax</span>
              <Input value={prescriber.fax ?? ''} onChange={(e) => setPrescriber({ ...prescriber, fax: e.target.value })} /></label>
          </div>

          {/* The practice address, as a line rather than six inputs — it is the
              facility's and is right nearly always. */}
          <div className="rounded-md bg-slate-50 px-3 py-2">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <span className={field}>Practice address</span>
                <p className="text-sm text-slate-700">
                  {[a.line1, a.city, [a.state, a.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ') || (
                    <span className="text-amber-700">No address on this facility.</span>
                  )}
                </p>
                {addressIncomplete && (
                  <p className="mt-0.5 text-[11px] text-amber-700">
                    {a.line1.trim()
                      ? 'This facility has no city, state or ZIP on file — the pharmacy needs them separately. Fill them in below.'
                      : 'Fill in the practice address below.'}
                  </p>
                )}
              </div>
              {!addressIncomplete && (
                <button type="button" onClick={() => setEditingAddress((v) => !v)}
                  className="shrink-0 text-[11px] text-blue-600 hover:underline">
                  {editingAddress ? 'Done' : 'Change'}
                </button>
              )}
            </div>

            {(editingAddress || addressIncomplete) && (
              <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-4">
                <label className="space-y-1 md:col-span-2"><span className={field}>Address *</span>
                  <Input value={a.line1} onChange={(e) => setPrescriber({ ...prescriber, address: { ...a, line1: e.target.value } })} /></label>
                <label className="space-y-1"><span className={field}>City *</span>
                  <Input value={a.city} onChange={(e) => setPrescriber({ ...prescriber, address: { ...a, city: e.target.value } })} /></label>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  <label className="space-y-1"><span className={field}>State *</span>
                    <Input value={a.state} onChange={(e) => setPrescriber({ ...prescriber, address: { ...a, state: e.target.value } })} placeholder="NV" /></label>
                  <label className="space-y-1"><span className={field}>ZIP *</span>
                    <Input value={a.zip} onChange={(e) => setPrescriber({ ...prescriber, address: { ...a, zip: e.target.value } })} /></label>
                </div>
              </div>
            )}
          </div>
        </div>
      </OrderSection>
    </div>
  );

  const patientName = [patient?.firstName, patient?.lastName].filter(Boolean).join(' ').trim();

  /** "Clinic" in the Ship to menu: the practice address from the Prescriber section. */
  const shipToChoices: ShipToChoice[] = [{
    key: 'clinic',
    label: `Clinic — ${prescriber.clinicName || 'practice address'}${a.line1 ? `, ${a.line1}, ${a.city}, ${a.state} ${a.zip}` : ' (fill it in under Prescriber)'}`,
    address: {
      // The package is still the patient's: the clinic receives it for them,
      // so the label names both — "Jane Doe c/o Vegas Clinic".
      recipientName: [patientName, prescriber.clinicName || 'the clinic'].filter(Boolean).join(' c/o '),
      line1: a.line1, city: a.city, state: a.state, zip: a.zip,
    },
    incompleteReason: addressIncomplete
      ? 'Practice address (the clinic ships to it) is incomplete — fill it in under Prescriber.'
      : null,
  }];

  const confirmSlot = items.length > 0 ? (
    <div className="space-y-1">
      <p className="font-semibold text-blue-900">Prescriptions:</p>
      {pricedLines.map(({ it, amount }) => (
        <p key={it.integrationProductId} className="text-blue-800">
          {it.drug} — {it.sig} (qty {it.quantity}, refills {it.refills}, {it.daySupply} days)
          {amount !== null && <> — {usd(amount)}</>}
        </p>
      ))}
      <p className="font-semibold text-blue-900">Total: {usd(orderTotal)}</p>
      <p className="pt-1 text-blue-800">
        Ships to the {shipType === 'ship_to_clinic' ? 'clinic' : 'patient'}, 2-day.
        {' '}Prescriber: {prescriber.firstName} {prescriber.lastName}{prescriber.npi ? ` (NPI ${prescriber.npi})` : ''}
      </p>
    </div>
  ) : null;

  return (
    <LabOrderModalCore
      config={config}
      patientId={patientId}
      open={isOpen}
      onClose={onClose}
      onOrdered={() => { /* handled by onSubmitted in config.submit */ }}
      // An edit keeps the order's own address; Clone / Re-send reuse theirs.
      initialShipTo={shipToOf(editOrder ?? shipFromOrder)}
      // A clinic delivery is fixed on its lines and not editable here.
      shipToChoices={isEdit ? undefined : shipToChoices}
      itemsSlot={editSlot ?? itemsSlot}
      confirmSlot={confirmSlot}
    />
  );
}
